"use client";

import type {
  QuickPayOutput,
  QuickPayReceiptContext,
} from "@tillix/api-contract/client-schemas";
import { useT } from "@tillix/i18n/react";
import { Alert, AlertDescription } from "@tillix/ui-atoms/atoms/alert";
import {
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
} from "@tillix/ui-atoms/framing/tabs";
import { formatCurrency } from "@tillix/utils/format";
import { TriangleAlert } from "lucide-react";
import { observer } from "mobx-react-lite";
import { PaymentAmountInput } from "@/components/atoms/payment-amount-input";
import QuickPayCardForm from "./QuickPayCardForm";
import QuickPayCheckForm from "./QuickPayCheckForm";

interface QuickPayFormProps {
  tenantId: string;
  externalId: string;
  nameOnAccount?: string;
  zipCode?: string;
  invoiceNumber?: string;
  isCashOnly?: boolean;
  amount: number;
  balance: number;
  onAmountChange: (amount: number) => void;
  onSuccess: (result: QuickPayOutput, context: QuickPayReceiptContext) => void;
  onCancel: () => void;
}

function QuickPayForm({
  amount,
  balance,
  externalId,
  isCashOnly,
  onAmountChange,
  ...rest
}: QuickPayFormProps) {
  const { t } = useT();
  return (
    <div className="w-full max-w-md">
      <h2 className="mb-1 font-semibold text-lg">
        {t("quickPay.makeAPayment")}
      </h2>
      <p className="mb-1 text-muted-foreground text-sm">
        {t("quickPay.accountEndingIn")} {externalId.slice(-4)} &middot;{" "}
        {t("quickPay.balance")} {formatCurrency(balance)}
      </p>

      <PaymentAmountInput
        amount={amount}
        balance={balance}
        className="mb-3 flex items-center gap-3"
        onAmountChange={(val) => {
          if (val !== undefined) onAmountChange(val);
        }}
      />

      {isCashOnly && (
        <Alert className="mb-3" variant="warning">
          <TriangleAlert className="size-4" />
          <AlertDescription>{t("quickPay.cashOnlyAlert")}</AlertDescription>
        </Alert>
      )}

      <Tabs className="w-full min-w-0" defaultValue="card">
        <TabsList className="w-full min-w-0 max-w-full">
          <TabsTab className="flex-1" value="card">
            {t("quickPay.debitOrCreditCard")}
          </TabsTab>
          {!isCashOnly && (
            <TabsTab className="flex-1" value="bank">
              {t("quickPay.bankAccount")}
            </TabsTab>
          )}
        </TabsList>
        <TabsPanel className="pt-2" value="card">
          <QuickPayCardForm amount={amount} externalId={externalId} {...rest} />
        </TabsPanel>
        {!isCashOnly && (
          <TabsPanel className="pt-2" value="bank">
            <QuickPayCheckForm
              amount={amount}
              externalId={externalId}
              {...rest}
            />
          </TabsPanel>
        )}
      </Tabs>
    </div>
  );
}

export default observer(QuickPayForm);
